import { useState } from 'react';
import {
  Box, Button, Card, CardContent, TextField, Typography, Alert,
  MenuItem, CircularProgress,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountsApi, Account } from '../api/accounts';

export default function TransferPage() {
  const queryClient = useQueryClient();
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const { data: res } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list(),
  });
  const accounts: Account[] = res?.data ?? [];
  const activeAccounts = accounts.filter((a) => a.status === 'ACTIVE');

  const mutation = useMutation({
    mutationFn: () => accountsApi.transfer({
      fromAccountId: fromId, toAccountId: toId, amount, description,
    }),
    onSuccess: () => {
      setSuccess('Transfer completed successfully.');
      setError('');
      setFromId(''); setToId(''); setAmount(''); setDescription('');
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setError(msg || 'Transfer failed. Please try again.');
      setSuccess('');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (fromId === toId) { setError('Source and destination accounts must be different.'); return; }
    if (parseFloat(amount) <= 0) { setError('Amount must be greater than zero.'); return; }
    mutation.mutate();
  };

  return (
    <Box sx={{ maxWidth: 500 }}>
      <Typography variant="h4" gutterBottom>Transfer Funds</Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <form onSubmit={handleSubmit}>
            <TextField select label="From Account" fullWidth required value={fromId}
              onChange={(e) => setFromId(e.target.value)} sx={{ mb: 2 }}>
              {activeAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.accountType} - {a.accountNumber} (${a.balance})
                </MenuItem>
              ))}
            </TextField>

            <TextField select label="To Account" fullWidth required value={toId}
              onChange={(e) => setToId(e.target.value)} sx={{ mb: 2 }}>
              {activeAccounts.filter((a) => a.id !== fromId).map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.accountType} - {a.accountNumber}
                </MenuItem>
              ))}
            </TextField>

            <TextField label="Amount" type="number" fullWidth required value={amount}
              onChange={(e) => setAmount(e.target.value)} sx={{ mb: 2 }}
              inputProps={{ min: '0.01', step: '0.01' }} />

            <TextField label="Description" fullWidth value={description}
              onChange={(e) => setDescription(e.target.value)} sx={{ mb: 2 }}
              placeholder="Optional transfer description" />

            <Button type="submit" variant="contained" fullWidth size="large"
              disabled={mutation.isPending}>
              {mutation.isPending ? <CircularProgress size={24} /> : 'Transfer'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
